import { sumBy } from "lodash";
import { initialClaim, InitialTimeSheetDataFn } from "./InitialTimeSheetData";
import { DRAFT, MAX_WORKING_HOURS, MIN_WORKING_HOURS, PENDING } from "./constants";
import { notification } from "antd";
import { useCallback, useEffect, useState } from "react";
import { setCacheTimesheet } from "redux/actions";
import { useDispatch, useSelector } from "react-redux";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import {
  getClientid,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import moment from "moment";
import { CALL_API } from "common/API";

const useTimesheetfns = ({
  personIncharge,
  OfficeSuperVisorUserId,
  currentWeek,
  form,
  setPersonInCharge,
  week,
  timesheetDataRes,
  onSubmit,
  isFetched,
  duplicateTimesheet,
}) => {
  const dispatch = useDispatch();

  const clientId = useSelector(getClientid);
  const userRoleId = useSelector(getUserRoleId);
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const monday = moment(currentWeek, "YYYY-MM-DD");
  const sunday = moment(currentWeek, "YYYY-MM-DD").add(6, "days");

  const { key: holidayKey, url: holdiayUrl } =
    queryKeys.getHolidayList(clientId);

  const { data: holidayDataRes } = useFetch(
    [holidayKey, monday, sunday],
    holdiayUrl,
    {
      enabled: !!clientId,
    },
    {
      start_date: monday,
      end_date: sunday,
    }
  );

  const holidays = holidayDataRes?.response;

  const hasHolidays = holidays?.find((holiday) =>
    holiday?.location_id?.includes(currentUser?.baselocationid)
  );

  const initialTimeSheet = InitialTimeSheetDataFn(currentWeek, hasHolidays);

  const getMatterCurrency = async (matterId) => {
    if (!matterId || matterId === "nc" || matterId === "ap") return null;
    try {
      const { data } = await CALL_API(`insyts-matter/viewmatter?matterid=${matterId}`, "get");
      return data?.financial_resourcePlan?.currency ?? null;
    } catch (error) {
      console.error("Failed to fetch matter currency:", error);
      return null;
    }
  };

  const [timeSheet, setTimeSheet] = useState(initialTimeSheet);

  const setInitialTimesheet = async () => {
    // setTimeSheet(initialTimeSheet);
    // form.setFieldsValue(initialTimeSheet);

    const [holidaysRes, leaveRes] = await Promise.all([
      CALL_API(`holiday-list/${clientId}`, "post", {
        start_date: monday,
        end_date: sunday,
      }),
      CALL_API(`leave-list/${clientId}`, "post", {
        status_id: "",
        officesupervisorid: [""],
        selected_role_id: userRoleId,
        start_date: monday,
        end_date: sunday,
      }),
    ]);

    const myLeaves = leaveRes.leaves.filter(
      (leave) =>
        leave.userid === userInfo.userid &&
        leave?.status_id === "5" &&
        leave?.having_cancel_request === "0"
    );

    const hasTakenLeave = myLeaves.length > 0;

    const hasHolidays = holidaysRes?.find((holiday) =>
      holiday?.location_id?.includes(currentUser?.baselocationid)
    );

    const initialTimeSheetWithHolidays = InitialTimeSheetDataFn(
      currentWeek,
      hasHolidays || hasTakenLeave
    );

    setTimeSheet(initialTimeSheetWithHolidays);
    form.setFieldsValue(initialTimeSheetWithHolidays);
  };

  const dayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  const isDateInRange = (date, startStr, endStr) => {
    const d = moment(date).startOf("day");
    const start = moment(startStr, "YYYY-MM-DD").startOf("day");
    const end = moment(endStr, "YYYY-MM-DD").startOf("day");
    return d.isSameOrAfter(start) && d.isSameOrBefore(end);
  };

  const setDuplicateTimesheetWithHolidays = async (duplicateEntries) => {
    const [holidaysRes, leaveRes] = await Promise.all([
      CALL_API(`holiday-list/${clientId}`, "post", {
        start_date: monday,
        end_date: sunday,
      }),
      CALL_API(`leave-list/${clientId}`, "post", {
        status_id: "",
        officesupervisorid: [""],
        selected_role_id: userRoleId,
        start_date: monday,
        end_date: sunday,
      }),
    ]);

    const targetHolidays = (holidaysRes || []).filter((holiday) =>
      holiday?.location_id?.includes(currentUser?.baselocationid)
    );

    const myLeaves = (leaveRes?.leaves || []).filter(
      (leave) =>
        leave.userid === userInfo.userid &&
        leave?.status_id === "5" &&
        leave?.having_cancel_request === "0"
    );

    // For each day of the TARGET week, work out if it's a holiday and/or leave day
    const dayInfo = dayKeys.map((key, idx) => {
      const date = monday.clone().add(idx, "days");
      const isSaturdayOrSunday = key === "sat" || key === "sun";
      const isHoliday =
        !isSaturdayOrSunday &&
        targetHolidays.some((h) =>
          isDateInRange(date, h.start_date, h.end_date)
        );
      const matchedLeave = myLeaves.find((l) =>
        isDateInRange(date, l.start_date, l.end_date)
      );
      const isLeave = !isSaturdayOrSunday && !!matchedLeave;
      const isHalfDayOnLastDay =
        isLeave &&
        moment(date).isSame(moment(matchedLeave.end_date, "YYYY-MM-DD"), "day") &&
        parseFloat(matchedLeave.number_of_days) % 1 !== 0;
      return { key, isHoliday, isLeave, isHalfDayOnLastDay };
    });

    const hasAnyHolidayOrLeave = dayInfo.some((d) => d.isHoliday || d.isLeave);

    // Reset hours to 0 on the duplicated C/NC rows for the target week's holiday/leave days
    const adjustedEntries = duplicateEntries.map((entry) => {
      const updated = { ...entry };
      dayInfo.forEach(({ key, isHoliday, isLeave }) => {
        if (isHoliday || isLeave) {
          updated[key] = 0;
        }
      });
      return updated;
    });

    if (!hasAnyHolidayOrLeave) {
      return adjustedEntries;
    }

    const hasHoliday = dayInfo.some((d) => d.isHoliday);
    const hasLeave = dayInfo.some((d) => d.isLeave);
    const taskLabel =
      hasHoliday && hasLeave
        ? "Public Holiday / Leave"
        : hasHoliday
          ? "Public Holiday"
          : "Leave";

    const apRow = {
      client: "ap",
      engagement: "ap",
      task: taskLabel,
      budget: "",
      actual_to_date: 0,
      currentWeek: currentWeek.format("YYYY-MM-DD"),
      claims: [],
      ncClient: null,
      subEngagement: null,
      remarks: "",
      status: "draft",
      approval: {},
      declined: {},
      isNew: false,
    };

    dayInfo.forEach(({ key, isHoliday, isLeave, isHalfDayOnLastDay }) => {
      if (key === "sat" || key === "sun") {
        apRow[key] = 0;
      } else if (isHoliday || isLeave) {
        apRow[key] = isHalfDayOnLastDay ? 4 : 8;
      } else {
        apRow[key] = 0;
      }
    });
    apRow.sum = dayKeys.reduce((sum, key) => sum + (apRow[key] || 0), 0);

    return [apRow, ...adjustedEntries];
  };

  useEffect(() => {
    if (!isFetched) {
      return;
    }
    if (timesheetDataRes?.response?.response?.timeSheet) {
      setTimeSheet(timesheetDataRes?.response?.response?.timeSheet);
      form.setFieldsValue(timesheetDataRes?.response?.response?.timeSheet);
    } else if (duplicateTimesheet) {
      setDuplicateTimesheetWithHolidays(duplicateTimesheet).then((merged) => {
        setTimeSheet(merged);
        form.setFieldsValue(merged);
      });
    } else {
      setInitialTimesheet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetDataRes, form]);

  const generateTimeSheetData = (
    values,
    draft = false,
    originalTimeSheet = [],
    isSumbitDraftIsPending = false,
    isDeclinedSave = false,
    isAddedNewRow = false,
    isDeclineSubmit = false
  ) => {
    return Object.values(values).map((value, index) => {
      const personInCharge =
        personIncharge?.[index] || OfficeSuperVisorUserId || "";
      const isClientAP = value?.client === "ap";
      const original = originalTimeSheet?.[index];

      const hasChanged =
        original &&
        (original.mon !== parseFloat(value?.mon) ||
          original.tue !== parseFloat(value?.tue) ||
          original.wed !== parseFloat(value?.wed) ||
          original.thu !== parseFloat(value?.thu) ||
          original.fri !== parseFloat(value?.fri) ||
          original.sat !== parseFloat(value?.sat) ||
          original.sun !== parseFloat(value?.sun) ||
          original.remarks !== value?.remarks);
      const wasDeclined = original?.status === "declined";

      const status = draft
        ? DRAFT
        : wasDeclined && isDeclinedSave
          ? DRAFT
          : wasDeclined && isDeclineSubmit
            ? PENDING
            : original?.status === DRAFT ||
              (value?.status === "draft" && isAddedNewRow)
              ? DRAFT
              : original?.status === DRAFT ||
                (isSumbitDraftIsPending && value?.status === DRAFT)
                ? PENDING
                : value?.status || original?.status || PENDING;

      return {
        ...value,
        mon: parseFloat(value?.mon) || 0,
        tue: parseFloat(value?.tue) || 0,
        wed: parseFloat(value?.wed) || 0,
        thu: parseFloat(value?.thu) || 0,
        fri: parseFloat(value?.fri) || 0,
        sat: isClientAP ? 0 : parseFloat(value?.sat) || 0,
        sun: isClientAP ? 0 : parseFloat(value?.sun) || 0,
        sum: parseFloat(value?.sum) || 0,
        actual_to_date:
          value?.engagement === "nc" || value?.engagement === "ap"
            ? 0
            : parseFloat(value?.actual_to_date) || 0,
        claims: value?.claims || [],
        personInCharge,
        currentWeek: currentWeek.format("YYYY-MM-DD"),
        ncClient: value?.ncClient || null,
        subEngagement: value?.subEngagement || null,
        remarks: value?.remarks || "",
        status: isClientAP && isSumbitDraftIsPending ? "approved" : status,
        approval: original?.approval || {},
        declined: wasDeclined && hasChanged ? {} : original?.declined || {},
      };
    });
  };

  const isChargeAbleAmtValid = (timeSheet) => {
    let engagementValidation = timeSheet?.some(
      (item) => item?.engagement == undefined || item?.engagement == null
    );
    let clientNameValidation = timeSheet?.some((item) => item?.client == null);

    const daysOfWeek = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const dayNames = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const validateDayTotal = (total, dayName, isWeekend) => {
      if (clientNameValidation) {
        notification.error({
          message: `Client Field is Required`,
        });
        return false;
      }

      if (engagementValidation) {
        notification.error({
          message: `Engagement Field is Required`,
        });
        return false;
      }

      // Weekdays must be between 8 and 12 hours
      if (
        !isWeekend &&
        (total < MIN_WORKING_HOURS || total > MAX_WORKING_HOURS)
      ) {
        notification.error({
          message: `${dayName} total hours should be between 8 and 12`,
        });
        return false;
      }

      // Weekends (Saturday & Sunday) must be between 0 and 12 hours
      if (isWeekend && (total < 0 || total > MAX_WORKING_HOURS)) {
        notification.error({
          message: `${dayName} total hours should be between 0 and 12`,
        });
        return false;
      }

      return true;
    };

    // Loop through all days and validate each one
    for (let i = 0; i < daysOfWeek.length; i++) {
      const dayTotal = sumBy(timeSheet, daysOfWeek[i]);
      const isWeekend = daysOfWeek[i] === "sat" || daysOfWeek[i] === "sun";

      if (!validateDayTotal(dayTotal, dayNames[i], isWeekend)) {
        return false;
      }
    }

    return true;
  };

  // const handleAddMore = () => {
  //   if (timeSheet.length >= 20) {
  //     notification.error({
  //       message: "You can't add more than 20 Slots",
  //     });
  //     return;
  //   }
  //   const values = form.getFieldsValue(true);
  //   const actualTimeSheet = generateTimeSheetData(values);
  //   let data = [...actualTimeSheet];
  //   data.push(...InitialTimeSheetDataFn(currentWeek,false,true));
  //   setTimeSheet(data);
  //   form.setFieldsValue(data);
  // };

  const handleAddMore = () => {
    if (timeSheet.length >= 20) {
      notification.error({
        message: "You can't add more than 20 Slots",
      });
      return;
    }
    const values = form.getFieldsValue(true);
    const actualTimeSheet = generateTimeSheetData(values);
    let data = [...actualTimeSheet];
    const newRow = InitialTimeSheetDataFn(currentWeek, false, "draft", true);
    data.push(...newRow);
    setTimeSheet(data);
    form.setFieldsValue(data);
  };

  const handleAddMoreClaim = async (timeSheetIndex) => {
    const values = form.getFieldsValue(true);

    const claimsLength = values[timeSheetIndex]?.claims?.length || 0;

    if (claimsLength >= 5) {
      notification.error({
        message: "You can't add more than 5 claims",
      });
      return;
    }

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const matterId = data[timeSheetIndex]?.engagement;
    const currency = await getMatterCurrency(matterId);

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        value.claims.push({
          ...initialClaim,
          currency,
          claimsId: Date.now() + Math.floor(Math.random() * 10000),
        });
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handleClearClaim = async (timeSheetIndex, claimIndex) => {
    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const matterId = data[timeSheetIndex]?.engagement;
    const currency = await getMatterCurrency(matterId);

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        return {
          ...value,
          claims: value.claims
            .map((claim, i) => {
              if (i === claimIndex) {
                return claimIndex === 0 ? { ...initialClaim, currency } : null;
              }
              return claim;
            })
            .filter((claim) => claim !== null),
        };
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handleResetClaim = async (timeSheetIndex) => {
    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const matterId = data[timeSheetIndex]?.engagement;
    const currency = await getMatterCurrency(matterId);

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        return {
          ...value,
          claims: [{ ...initialClaim, currency }],
        };
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handleRemoveClaim = (timeSheetIndex) => {
    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        return {
          ...value,
          claims: [],
        };
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handlePersonInCharge = useCallback((index, value) => {
    setPersonInCharge((prev) => {
      const data = [...prev];
      data[index] = value;
      return data;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearTimesheet = (index, value) => {
    if (index === 0 && Object.keys(value).length === 1) {
      setTimeSheet(initialTimeSheet);
      form.setFieldsValue(initialTimeSheet);
      notification.info({
        message: "Timesheet Section Cleared",
      });
      return;
    }

    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    if (index >= 0 && index < data.length) {
      data = data.filter((_, i) => i !== index);
      setTimeSheet(data);
      form.setFieldsValue(data);
      notification.info({
        message: "Timesheet Section Removed",
      });
    } else {
      console.error("Invalid index:", index);
    }
  };

  const handleSaveTimesheet = () => {
    const values = form.getFieldsValue(true);
    const originalTimeSheet = timesheetDataRes?.response?.response?.timeSheet;
    const timeSheetData = generateTimeSheetData(
      values,
      true,
      originalTimeSheet
    ).map((entry) => ({
      ...entry,
      status: DRAFT,
      approval: {},
      declined: {},
    }));
    dispatch(setCacheTimesheet(week, timeSheetData));
    onSubmit(values, true, true, originalTimeSheet);
    // notification.success({
    //   message: "Timesheet Saved",
    // });
  };

  const handleSubmitTimesheet = () => {
    const values = form.getFieldsValue(true);
    const originalTimeSheet = timesheetDataRes?.response?.response?.timeSheet;
    const formattedData = generateTimeSheetData(
      values,
      false,
      originalTimeSheet,
      true
    );
    onSubmit(formattedData);
  };

  return {
    timeSheet,
    setTimeSheet,
    initialTimeSheet,
    generateTimeSheetData,
    handleAddMore,
    handleAddMoreClaim,
    handleClearClaim,
    handleResetClaim,
    handleRemoveClaim,
    handlePersonInCharge,
    handleClearTimesheet,
    handleSaveTimesheet,
    isChargeAbleAmtValid,
    handleSubmitTimesheet,
  };
};

export default useTimesheetfns;